import { useEffect, useRef } from 'react';
import { captureAppError } from '@leagueplatform/observability';
import {
  getAbsoluteRootUrl,
  handleFetchResult,
  openDecodedLink,
  openLoadingTab,
  resolveAbsoluteUrl,
  shouldIntercept,
  fetchDataWithAppropriateToken,
} from 'utils/helpers';
import { isImpersonationSession } from 'utils/auth/impersonation-storage';

interface InterceptPattern {
  path: string;
  contentType: 'text/html' | 'application/pdf';
  useRootUrl?: boolean;
  partnerId?: string;
}

interface LinkInterceptorConfig {
  patternsToIntercept: InterceptPattern[];
  rootUrl: string;
  /** Called instead of opening the SSO tab when the current session is impersonated. */
  onRestrictedAction?: () => void;
}

/**
 * Opens URL in new tab with proper auth token based on pattern config
 */
const openWithAuthToken = async (
  href: string,
  pattern: InterceptPattern,
  rootUrl: string,
): Promise<void> => {
  const targetUrl = pattern.useRootUrl
    ? new URL(href, getAbsoluteRootUrl(rootUrl)).href
    : resolveAbsoluteUrl(href);

  const tab = openLoadingTab();
  const result = await fetchDataWithAppropriateToken(
    targetUrl,
    pattern.contentType,
    pattern.partnerId,
  );
  handleFetchResult(tab, result, pattern.contentType);
};

export function useInterceptLinkClick({
  patternsToIntercept,
  rootUrl,
  onRestrictedAction,
}: LinkInterceptorConfig): void {
  const rootUrlRef = useRef(rootUrl);
  rootUrlRef.current = rootUrl;

  const onRestrictedActionRef = useRef(onRestrictedAction);
  onRestrictedActionRef.current = onRestrictedAction;

  // Intercept programmatic navigation (buttons using navigate())
  useEffect(() => {
    const historyObj = window.history;
    const originalPushState = historyObj.pushState.bind(historyObj);
    const originalReplaceState = historyObj.replaceState.bind(historyObj);

    const interceptNavigation = (
      original: typeof historyObj.pushState,
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) => {
      const urlStr = url?.toString() || '';

      const matchedPattern = patternsToIntercept.find(
        (pattern) =>
          pattern.contentType === 'text/html' && urlStr.includes(pattern.path),
      );

      if (matchedPattern) {
        if (isImpersonationSession() && onRestrictedActionRef.current) {
          onRestrictedActionRef.current();
          return;
        }
        openWithAuthToken(urlStr, matchedPattern, rootUrlRef.current);
        return;
      }

      original(data, unused, url);
    };

    historyObj.pushState = (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) => {
      interceptNavigation(originalPushState, data, unused, url);
    };

    historyObj.replaceState = (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) => {
      interceptNavigation(originalReplaceState, data, unused, url);
    };

    return () => {
      historyObj.pushState = originalPushState;
      historyObj.replaceState = originalReplaceState;
    };
  }, [patternsToIntercept]);

  // Intercept anchor tag clicks
  useEffect(() => {
    const handleClick = async (event: MouseEvent): Promise<void> => {
      let element = event.target as HTMLElement | null;

      while (element && element.tagName !== 'A' && element.parentElement) {
        element = element.parentElement;
      }

      if (!element || element.tagName !== 'A') {
        return;
      }

      const href = element.getAttribute('href');
      if (!href) {
        return;
      }

      // Handle tel: links
      if (href.startsWith('tel:')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Handle benefits phone number links (e.g., /benefits/855-417-7102)
      if (
        href.includes('/benefits/') &&
        /\/benefits\/\d{3}-\d{3}-\d{4}/.test(href)
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      try {
        const absoluteHref = resolveAbsoluteUrl(href);
        const path = new URL(absoluteHref).pathname;

        const matchedPattern = patternsToIntercept.find(
          ({ path: pattern, contentType }) => {
            const willIntercept =
              contentType === 'text/html'
                ? shouldIntercept(element as HTMLAnchorElement, pattern)
                : path
                    .split('/')
                    .filter(Boolean)
                    .includes(pattern.replace(/^\/|\/$/g, ''));
            return willIntercept;
          },
        );

        if (matchedPattern) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (isImpersonationSession() && onRestrictedActionRef.current) {
            onRestrictedActionRef.current();
            return;
          }

          await openWithAuthToken(href, matchedPattern, rootUrl);
          return;
        }

        if (absoluteHref.includes('open-link?')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          openDecodedLink(absoluteHref);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        captureAppError(error instanceof Error ? error : new Error(message));
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    document.addEventListener('auxclick', handleClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      document.removeEventListener('auxclick', handleClick, { capture: true });
    };
  }, [patternsToIntercept, rootUrl]);
}
